import BaseAPI from 'api/api'
import type { ManualPage, ManualIndexEntry } from 'features/update/manuals/manual.types'

const path = '/manuals'

/** In-memory caches */
let menuTreeCache: ManualMenuGroup[] | null = null
let manualIndexCache: ManualIndexEntry[] | null = null
const manualCache = new Map<string, ManualPage>()

export interface ManualMenuGroup {
  menuFirstKey: string
  labelKey: string
  children: ManualMenuChild[]
}

export interface ManualMenuChild {
  menuSecondKey: string
  labelKey: string
  items?: { menuThirdKey: string; labelKey: string }[]
}

export default class ManualApi extends BaseAPI {
  /** Fetch menu tree for navigation */
  static async getMenuTree(): Promise<ManualMenuGroup[]> {
    if (menuTreeCache) return menuTreeCache
    try {
      const res = await this.api.get(`${path}/menu-tree`)
      const data: ManualMenuGroup[] = res.data || []
      menuTreeCache = data
      return data
    } catch (err) {
      console.error('Failed to fetch manual menu tree:', err)
      return []
    }
  }

  /** Fetch lightweight manual index for search + menu matching */
  static async getManualIndex(): Promise<ManualIndexEntry[]> {
    if (manualIndexCache) return manualIndexCache
    try {
      const res = await this.api.get(`${path}/index`)
      const data: ManualIndexEntry[] = res.data || []
      manualIndexCache = data
      return data
    } catch (err) {
      console.error('Failed to fetch manual index:', err)
      return []
    }
  }

  /** Fetch a single manual page by id */
  static async getManualById(id: string): Promise<ManualPage | null> {
    const cached = manualCache.get(id)
    if (cached) return cached

    try {
      const res = await this.api.get(`${path}/data/${id}`)
      const manual: ManualPage = res.data
      if (manual && manual.steps) {
        manual.steps = manual.steps.map((step) => ({
          ...step,
          screenshot: step.screenshot
            ? this.getScreenshotUrl(step.screenshot)
            : undefined,
        }))
      }
      manualCache.set(id, manual)
      return manual
    } catch (err) {
      console.error(`Failed to fetch manual "${id}":`, err)
      return null
    }
  }

  /** Build authenticated screenshot URL via backend proxy */
  static getScreenshotUrl(relativePath: string): string {
    if (relativePath.startsWith('http')) return relativePath
    const clean = relativePath.replace(/^\/?(manuals\/)?(screenshots\/)?/, '')
    return `${this.api.defaults.baseURL}${path}/screenshots/${clean}`
  }

  /** Fetch screenshot as blob (for PDF export / authenticated image loading) */
  static async getScreenshotBlob(url: string): Promise<string> {
    try {
      const res = await this.api.get(url, { responseType: 'blob' })
      return URL.createObjectURL(res.data)
    } catch (err) {
      console.error('Failed to fetch screenshot:', err)
      return url
    }
  }

  /** Convert screenshot URL to base64 data URI (for PDF export) */
  static async getScreenshotBase64(url: string): Promise<string> {
    try {
      const res = await this.api.get(url, { responseType: 'arraybuffer' })
      const contentType = res.headers['content-type'] || 'image/png'
      const base64 = btoa(
        new Uint8Array(res.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
      )
      return `data:${contentType};base64,${base64}`
    } catch (err) {
      console.error('Failed to convert screenshot to base64:', err)
      return url
    }
  }

  /** Clear all caches (useful on logout or manual refresh) */
  static clearCache() {
    menuTreeCache = null
    manualIndexCache = null
    manualCache.clear()
  }
}
